import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { attachmentsActions } from '../../../../state/ducks/attachments';
import { Attachment, AttachmentDownloadResponse, AttachmentType, WatermarkRequest, WatermarkRequestBody, WatermarkRequestStatus } from '../../../../state/ducks/attachments/types';
import { authSelectors } from '../../../../state/ducks/auth';
import { AsyncState, AsyncStatus } from '../../../../state/types';
import { toastError } from '../../../../ui/components/notifications';
import useActionCreator from '../../../hooks/useActionCreator';
import useAsync from '../../../hooks/useAsync';
import useMqttSubscription from '../../../hooks/useMqttSubscription';
import ViewFilePresenter from './presenter';
import { ViewFileContainerProps } from './utils/types';

const EMPTY_ATTACHMENT: Attachment = {
  id: '',
  name: '',
  type: '',
  s3link: '',
  lineType: '',
  fileSizeS3Link: 0,
  fileSizeS3LinkCleanCopy: 0,
  hasWatermarks: false,
};

const ViewFileContainer: React.FunctionComponent<ViewFileContainerProps> = ({
  attachment = EMPTY_ATTACHMENT,
  attachments,
  isOpen,
  handleClose,
  password,
  documentRevisionId,
  documentRevisionStatus,
}) => {
  const [signedUrl, setSignedUrl] = useState('');
  const [watermarkId, setWatermarkId] = useState('');
  const [watermarkViewState, setWatermarkViewState] = useState<AsyncState>({
    status: AsyncStatus.Idle,
    message: '',
  });
  const download = useActionCreator(attachmentsActions.download);
  const downloadWithPassword = useActionCreator(attachmentsActions.downloadRedLine);
  const requestWatermark = useActionCreator(attachmentsActions.requestWatermarkedFile);
  const checkWatermark = useActionCreator(attachmentsActions.checkWatermarkStatus);
  const employeeId = useSelector(authSelectors.currentEmployeeId);
  const isUnreleased = [
    'DRAFT',
    'PENDING_CHANGE',
    'SUBMITTED',
    'IN_REVIEW',
    'APPROVED',
  ].includes(String(documentRevisionStatus));

  const shouldWatermark = Boolean(
    isOpen && documentRevisionId && isUnreleased && attachment.type?.toLowerCase() === 'pdf',
  );

  const async = useAsync<AttachmentDownloadResponse>({
    onSuccess: (downloadInfo) => downloadInfo && setSignedUrl(downloadInfo.url),
    onError: toastError,
  });

  const watermarkAsync = useAsync<WatermarkRequest>({
    onSuccess: (request = {} as WatermarkRequest) => setWatermarkId(request.id),
    onError: (message = 'Watermark error') => {
      setWatermarkViewState({ status: AsyncStatus.Error, message });
      toastError(message);
    },
  });

  const watermarkStatusAsync = useAsync<WatermarkRequest>({
    onSuccess: (request = {} as WatermarkRequest) => {
      if (request.status === WatermarkRequestStatus.Done && request.presignedUrl) {
        setSignedUrl(request.presignedUrl);
        setWatermarkId('');
        setWatermarkViewState({ status: AsyncStatus.Success, message: '' });
      } else if (request.status === WatermarkRequestStatus.Done) {
        const message = 'Watermarked PDF URL is unavailable';
        setWatermarkViewState({ status: AsyncStatus.Error, message });
        toastError(message);
      } else if (request.status === WatermarkRequestStatus.Failed) {
        const message = 'Unable to generate Draft watermark';
        setWatermarkViewState({ status: AsyncStatus.Error, message });
        toastError(message);
        setWatermarkId('');
      }
    },
    onError: (message) => {
      setWatermarkViewState({ status: AsyncStatus.Error, message: message ?? '' });
      toastError(message);
      setWatermarkId('');
    },
  });

  const watermarkTopic = useMemo(
    () => watermarkId && employeeId ? `${employeeId}/attachment_watermark/${watermarkId}/update` : '',
    [employeeId, watermarkId],
  );

  const onWatermarkReady = useCallback(() => {
    watermarkStatusAsync.start(checkWatermark, watermarkId, watermarkStatusAsync);
  }, [checkWatermark, watermarkId, watermarkStatusAsync]);
  useMqttSubscription(watermarkTopic, onWatermarkReady);

  useEffect(() => {
    setSignedUrl('');
    setWatermarkId('');
    if (shouldWatermark) {
      setWatermarkViewState({ status: AsyncStatus.Active, message: '' });
      const body: WatermarkRequestBody = {
        attachment: { id: attachment.id },
        type: AttachmentType.Attached,
        documentRevision: { id: documentRevisionId as string },
      };
      watermarkAsync.start(requestWatermark, body, watermarkAsync);
      return;
    }
    setWatermarkViewState({ status: AsyncStatus.Idle, message: '' });

    if (isOpen && password) {
      async.start(downloadWithPassword, attachment, async, undefined, password.password);
      return;
    } else if (isOpen && !password) {
      async.start(download, attachment, async, undefined);
      return;
    }
    setSignedUrl('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, attachment, shouldWatermark, documentRevisionId]);

  return (
    <ViewFilePresenter
      handleClose={handleClose}
      isOpen={isOpen}
      asyncState={shouldWatermark ? watermarkViewState : async.asyncState}
      url={signedUrl}
      attachment={attachment}
      attachments={attachments}
    />
  );
};

export default ViewFileContainer;
